import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useNotificationTrackersByCodename } from '/@/hooks/notification-tracker';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';

export const useAutomaticCloseCropMonitoringAlertConditions = ({ field }: { field: Field }) => {
    const latestCropMonitoring = (field.cropMonitoring || []).sort(
        (a: CropMonitoring, b: CropMonitoring) => new Date(b.created).getTime() - new Date(a.created).getTime()
    )[0];

    const notificationTrackersQueryModal = useNotificationTrackersByCodename(
        { codename: `cm_automatic_close_modal_${latestCropMonitoring?.id}` },
        { enabled: !field.activeCropMonitoring && !!latestCropMonitoring }
    );
    const notificationTrackersQueryAlert = useNotificationTrackersByCodename(
        { codename: `cm_automatic_close_alert_${latestCropMonitoring?.id}` },
        { enabled: !field.activeCropMonitoring && !!latestCropMonitoring }
    );
    const notificationTrackersModal = notificationTrackersQueryModal.data?.results?.filter((r) => !r.readedOn)[0];
    const notificationTrackersAlert = notificationTrackersQueryAlert.data?.results?.filter((r) => !r.readedOn)[0];

    // If there is no crop monitoring active
    if (!field.activeCropMonitoring) {
        // Check if the latest crop monitoring in this field was automatically closed
        if (latestCropMonitoring && !latestCropMonitoring.active) {
            return {
                notificationTrackersModal: notificationTrackersModal?.id || undefined,
                notificationTrackersAlert: notificationTrackersAlert?.id || undefined,
            };
        }
    }

    return {
        notificationTrackersModal: undefined,
        notificationTrackersAlert: undefined,
    };
};

export const useCornCropMonitoringAlertConditions = ({ cropMonitoring }: { cropMonitoring?: CropMonitoring }) => {
    return useMemo(() => {
        const cornCropMonitoring = cropMonitoring?.cornCropMonitoring;

        if (!cornCropMonitoring || cropMonitoring?.harvestDate)
            // If CM is active and has harvest date, is because the user finished the campaign manually
            return {
                showPrepareForYourHarvestAlert: false,
                showDoYouHarvestYourFieldAlert: false,
                showHowDidYourHarvestGoAlert: false,
            };

        // Alerts to show when there is a crop monitoring active

        const phenology = cornCropMonitoring.phenology;

        const expectedMaturityDate = dayjs(cornCropMonitoring.expectedMaturityDate);
        const expectedHarvestDate = cornCropMonitoring.expectedHarvestDate
            ? dayjs(cornCropMonitoring.expectedHarvestDate)
            : dayjs(expectedMaturityDate).add(60, 'day');

        // Prepare for your harvest alert
        const isMaturity = phenology === 'maturity';
        const maturityDatePlus30Days = dayjs(expectedMaturityDate).add(30, 'day');
        const showPrepareForYourHarvestAlert = isMaturity && dayjs().isAfter(maturityDatePlus30Days);

        // Do you harvest your field alert and modal
        const showDoYouHarvestYourFieldAlert = isMaturity && dayjs().isAfter(expectedHarvestDate);

        // How did your harvest go alert
        const showHowDidYourHarvestGoAlert = isMaturity && dayjs().isAfter(expectedHarvestDate.add(15, 'day'));

        return {
            showPrepareForYourHarvestAlert,
            showDoYouHarvestYourFieldAlert,
            showHowDidYourHarvestGoAlert,
        };
    }, [cropMonitoring]);
};

export const useGeneralCropMonitoringAlertConditions = ({ cropMonitoring }: { cropMonitoring?: CropMonitoring }) => {
    return useMemo(() => {
        if (!cropMonitoring || cropMonitoring?.harvestDate)
            // If CM is active and has harvest date, is because the user finished the campaign manually
            return { showDoYouHarvestYourFieldAlert: false };

        const zone = cropMonitoring.zone;
        if (!zone) {
            return { showDoYouHarvestYourFieldAlert: false };
        }

        const estimatedHarvestDate = dayjs(cropMonitoring.plantingDate).add(zone.estimatedCycleTime, 'day');

        // Do you harvest your field alert and modal
        const showDoYouHarvestYourFieldAlert = dayjs().isAfter(estimatedHarvestDate);

        return { showDoYouHarvestYourFieldAlert };
    }, [cropMonitoring]);
};
